// TablaSolicitudes.js
import { useState, useEffect } from "react";
import { Table, Button, Form, Modal, Badge, OverlayTrigger, Tooltip, InputGroup } from "react-bootstrap";

const BASE_URL = "https://sectorb12.info/api";

const TablaSolicitudes = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [selectores, setSelectores] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [solicitudAEliminar, setSolicitudAEliminar] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const token = localStorage.getItem("token");

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    fetchSolicitudes();
    fetchNotificaciones();
  }, []);

  const fetchSolicitudes = async () => {
    try {
      const response = await fetch(`${BASE_URL}/solicitudes/completas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setSolicitudes(await response.json());
    } catch (error) {
      console.error("Error al obtener solicitudes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificaciones = async () => {
    try {
      const response = await fetch(`${BASE_URL}/notificaciones`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) setNotificaciones(await response.json());
    } catch (error) {
      console.error("Error al obtener notificaciones:", error);
    }
  };

  const getIndicadoresSolicitud = (solicitudId) => {
    const notisSolicitud = notificaciones.filter((n) => n.SolicitudID === solicitudId);
    const indicadores = [];

    const subsanadas = notisSolicitud.filter((n) => n.Subsanada);
    if (subsanadas.length > 0) {
      indicadores.push(
        <OverlayTrigger key="subsanada" placement="top" overlay={<Tooltip>Documentación de subsanación aportada</Tooltip>}>
          <Badge bg="warning" className="me-1">
            <i className="bi bi-paperclip"></i> Subsanación aportada
          </Badge>
        </OverlayTrigger>
      );
    }

    const pendientes = notisSolicitud.filter((n) => !n.Firmado && n.Tipo !== "justificante");
    if (pendientes.length > 0) {
      indicadores.push(
        <OverlayTrigger key="pendiente" placement="top" overlay={<Tooltip>{pendientes.length} notificación(es) pendiente(s) de firma</Tooltip>}>
          <Badge bg="danger" className="me-1">
            <i className="bi bi-bell"></i> {pendientes.length} pendiente(s)
          </Badge>
        </OverlayTrigger>
      );
    }

    const sinNotificaciones = notisSolicitud.filter((n) => n.Tipo !== "justificante").length === 0;
    if (sinNotificaciones) {
      indicadores.push(
        <Badge key="nueva" bg="info" className="me-1">
          <i className="bi bi-star"></i> Nueva
        </Badge>
      );
    }

    return indicadores;
  };

  const handleDownload = (base64Data, fileName) => {
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    const blob = new Blob(byteArrays, { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const actualizarEstado = async (id, nuevoEstado) => {
    try {
      const response = await fetch(`${BASE_URL}/solicitudes/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ Estado: nuevoEstado }),
      });
      if (response.ok) {
        const data = await response.json();
        setSolicitudes((prev) =>
          prev.map((s) => s.SolicitudID === id ? { ...s, Estado: data.Estado } : s)
        );
      }
    } catch (error) {
      console.error("Error en la solicitud PUT:", error);
    }
  };

  const handleDelete = (id) => {
    setSolicitudAEliminar(id);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${BASE_URL}/solicitudes/${solicitudAEliminar}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setSolicitudes((prev) => prev.filter((s) => s.SolicitudID !== solicitudAEliminar));
        setShowModal(false);
        alert("Solicitud eliminada con éxito.");
      }
    } catch (error) {
      console.error("Error al eliminar:", error);
    }
  };

  const activarSelector = (id) => setSelectores({ ...selectores, [id]: true });
  const desactivarSelector = (id) => setSelectores({ ...selectores, [id]: false });

  const renderEstado = (estado) => {
    const colores = { pendiente: "warning", "en proceso": "primary", completada: "success", rechazada: "danger" };
    return <Badge bg={colores[estado] || "secondary"} className="text-capitalize">{estado}</Badge>;
  };

  const solicitudesFiltradas = solicitudes.filter((s) => {
    const term = filtro.toLowerCase();
    const coincideTexto =
      !term ||
      s.NumeroRegistro?.toLowerCase().includes(term) ||
      s.Usuario?.Nombre?.toLowerCase().includes(term) ||
      s.Usuario?.Apellidos?.toLowerCase().includes(term) ||
      s.Usuario?.Num_Identificacion?.toLowerCase().includes(term);
    const coincideEstado = !estadoFiltro || s.Estado === estadoFiltro;
    return coincideTexto && coincideEstado;
  });

  if (loading) return <p className="mt-3 text-muted">Cargando solicitudes...</p>;

  return (
    <div className="mt-4">
      <div className="d-flex flex-column flex-md-row gap-2 mb-3">
        <InputGroup>
          <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
          <Form.Control
            placeholder="Buscar por DNI, nombre o número de registro…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
          {filtro && <Button variant="outline-secondary" onClick={() => setFiltro("")}>✕</Button>}
        </InputGroup>
        <Form.Select
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value)}
          style={{ maxWidth: 200 }}
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="en proceso">En proceso</option>
          <option value="completada">Completada</option>
          <option value="rechazada">Rechazada</option>
        </Form.Select>
      </div>

      {solicitudesFiltradas.length === 0 ? (
        <p className="text-muted">No hay solicitudes que coincidan con el filtro.</p>
      ) : (
      <div className="table-responsive">
        <Table hover className="rounded-3 shadow-sm align-middle">
          <thead className="table-light">
            <tr>
              <th>Nº Registro</th>
              <th>Usuario</th>
              <th>Descripción</th>
              <th>Fecha de solicitud</th>
              {/* <th>Archivo adjunto</th> */}
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {solicitudesFiltradas.map((s) => (
              <tr key={s.SolicitudID}>
                <td><code className="small text-muted">{s.NumeroRegistro || "—"}</code></td>
                <td>{s.Usuario.Nombre} {s.Usuario.Apellidos}</td>
                <td>
                  <div>{s.Descripcion}</div>
                  <div className="mt-1 d-flex flex-wrap gap-1">
                    {getIndicadoresSolicitud(s.SolicitudID)}
                  </div>
                </td>
                <td>{new Date(s.FechaSolicitud).toLocaleString("es-ES")}</td>
                {/* <td>
                  <div className="small text-muted">{s.NombreDocumento}</div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mt-1"
                    onClick={() => handleDownload(s.Documentos, s.NombreDocumento)}
                  >
                    <i className="bi bi-download"></i> Descargar
                  </Button>
                </td> */}
                <td>
                  {selectores[s.SolicitudID] ? (
                    <Form.Select
                      size="sm"
                      value={s.Estado}
                      onChange={(e) => actualizarEstado(s.SolicitudID, e.target.value)}
                    >
                      <option value="pendiente">Pendiente</option>
                      <option value="en proceso">En proceso</option>
                      <option value="completada">Completada</option>
                      <option value="rechazada">Rechazada</option>
                    </Form.Select>
                  ) : (
                    renderEstado(s.Estado)
                  )}
                </td>
                <td>
                  <div className="d-flex flex-column gap-1">
                    {!selectores[s.SolicitudID] ? (
                      <Button variant="warning" size="sm" onClick={() => activarSelector(s.SolicitudID)}>
                        <i className="bi bi-pencil"></i> Cambiar Estado
                      </Button>
                    ) : (
                      <Button variant="warning" size="sm" onClick={() => { actualizarEstado(s.SolicitudID, s.Estado); desactivarSelector(s.SolicitudID); }}>
                        <i className="bi bi-check-lg"></i> Aceptar
                      </Button>
                    )}
                    <Button variant="danger" size="sm" onClick={() => handleDelete(s.SolicitudID)}>
                      <i className="bi bi-trash"></i> Eliminar
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>¿Estás seguro de que deseas eliminar esta solicitud? Esta acción no se puede deshacer.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={confirmDelete}>Eliminar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TablaSolicitudes;